import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, MessageCircle, ShoppingCart } from "lucide-react";
import { api, cachedGet, trackWaClick } from "@/lib/api";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { imgVariant } from "@/lib/img";
import { cashPrice } from "@/lib/pricing";
import { useSettings } from "@/context/SettingsContext";

// Pop-up promocional: produto real, programável, com limite de exibição por visitante.
export const PromoPopup = () => {
  const settings = useSettings();
  const location = useLocation();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/checkout")) return;
    const page = location.pathname === "/" ? "home" : location.pathname.startsWith("/produtos") || location.pathname.startsWith("/categoria") ? "produtos" : "other";
    cachedGet("/popups/active", 300000).then((data) => {
      const eligible = (data || []).filter((p) => p.pages === "all" || p.pages === page);
      if (!eligible.length) return;
      const p = eligible[0];
      // limite de repetição: no máximo 2x por visitante por pop-up
      const key = `hg_popup_${p.id}`;
      const shown = Number(localStorage.getItem(key) || 0);
      if (shown >= 2) return;
      const t = setTimeout(() => {
        localStorage.setItem(key, String(shown + 1));
        setPopup(p);
        setVisible(true);
      }, Math.max(3, p.delay_seconds || 8) * 1000);
      return () => clearTimeout(t);
    }).catch(() => {});
  }, [location.pathname]);

  if (!visible || !popup?.product) return null;
  const prod = popup.product;
  const img = (prod.images || [])[0];

  return (
    <div className="fixed inset-x-4 bottom-4 sm:inset-auto sm:right-5 sm:bottom-5 sm:max-w-xs z-[70] animate-in slide-in-from-bottom-4" data-testid="promo-popup" data-anim="popup">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden relative">
        <button
          onClick={() => setVisible(false)}
          aria-label="Fechar"
          className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center text-gray-500 hover:text-gray-800"
          data-testid="promo-popup-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex gap-3 p-3">
          {img && (
            <img src={imgVariant(img, "thumb")} alt={prod.name} className="w-20 h-20 rounded-xl object-contain bg-gray-50 shrink-0" loading="lazy" decoding="async" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wide text-brand">{popup.headline}</p>
            <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mt-0.5">{prod.name}</p>
            <p className="font-heading font-extrabold text-lg text-[#D71920] mt-1">{fmtBRL(cashPrice(prod))}</p>
            <div className="flex gap-1.5 mt-2">
              {popup.mode !== "whatsapp" && (
                <Link
                  to={`/produto/${prod.id}`}
                  onClick={() => setVisible(false)}
                  className="flex-1 text-center text-xs font-bold bg-brand text-white rounded-full py-2 hover:bg-brand/90 transition-colors"
                  data-testid="promo-popup-buy"
                >
                  <ShoppingCart className="w-3.5 h-3.5 inline -mt-0.5" /> Ver produto
                </Link>
              )}
              <a
                href={buildWhatsAppUrl(`Olá! Vi a oferta de ${prod.name} no site e quero aproveitar!`, settings.whatsapp_number)}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWaClick(prod.id, "popup")}
                className="flex-1 text-center text-xs font-bold bg-green-600 text-white rounded-full py-2 hover:bg-green-700 transition-colors"
                data-testid="promo-popup-whatsapp"
              >
                <MessageCircle className="w-3.5 h-3.5 inline -mt-0.5" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
